import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, RotateCcw, Truck, Shirt, FolderTree, Layers,
  Boxes, Users, TicketPercent, CreditCard, Star, FileText, BarChart3, Share2, Settings, LogOut, ScrollText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["", "Dashboard", LayoutDashboard, "*"],
  ["orders", "Orders", ShoppingCart, "orders"],
  ["returns", "Returns", RotateCcw, "returns"],
  ["shipping", "Shipping", Truck, "shipping"],
  ["products", "Products", Shirt, "products"],
  ["categories", "Categories", FolderTree, "categories"],
  ["collections", "Collections", Layers, "collections"],
  ["inventory", "Inventory", Boxes, "inventory"],
  ["customers", "Customers", Users, "customers"],
  ["discounts", "Discounts", TicketPercent, "products"],
  ["payments", "Payments", CreditCard, "orders"],
  ["reviews", "Reviews", Star, "reviews"],
  ["content", "Website Content", FileText, "content"],
  ["analytics", "Analytics", BarChart3, "*"],
  ["audit", "Audit Logs", ScrollText, "*"],
  ["social", "Social Media", Share2, "content"],
  ["settings", "Settings", Settings, "*"],
];

const ROLE_PERMS = {
  super_admin: ["*"], store_manager: ["products", "inventory", "orders", "returns", "categories", "collections", "reviews"],
  order_manager: ["orders", "shipping"], content_manager: ["products", "content", "collections", "categories", "media"],
  customer_support: ["customers", "orders", "returns", "reviews"],
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  React.useEffect(() => { if (user === false || (user && user.role === "customer")) nav("/admin/login"); }, [user, nav]);
  if (!user || user.role === "customer") return <div className="min-h-screen flex items-center justify-center text-sharoa-muted">Loading…</div>;

  const perms = ROLE_PERMS[user.role] || [];
  const allowed = (p) => perms.includes("*") || perms.includes(p);
  const items = NAV.filter(([, , , perm]) => perm === "*" || allowed(perm));

  return (
    <div className="min-h-screen flex bg-[#F7F4F2]">
      <aside className="w-60 bg-[#1F191B] text-white/80 flex flex-col fixed h-screen overflow-y-auto">
        <div className="p-5 border-b border-white/10">
          <img src="/logo.png" alt="SHAROA" className="h-12 brightness-0 invert" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-sharoa-rose mt-2">Admin Portal</p>
        </div>
        <nav className="flex-1 py-3">
          {items.map(([path, label, Icon]) => (
            <NavLink key={path} to={`/admin/${path}`} end={path === ""} data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${isActive ? "bg-sharoa-rose text-white" : "hover:bg-white/5"}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-3 px-5 py-4 text-sm border-t border-white/10 hover:bg-white/5"><LogOut size={17} /> Logout</button>
      </aside>
      <main className="flex-1 ml-60 p-6 lg:p-8">
        <div className="flex justify-between items-center mb-6">
          <span className="text-xs text-sharoa-muted">Signed in as <b className="text-sharoa-charcoal">{user.email}</b> · <span className="capitalize">{user.role?.replace("_", " ")}</span></span>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export function AdminCard({ children, className = "" }) {
  return <div className={`bg-white border border-sharoa-line p-5 ${className}`}>{children}</div>;
}
export function Badge({ children, tone = "rose" }) {
  const tones = { rose: "bg-sharoa-pale text-sharoa-rose", green: "bg-green-50 text-green-700", amber: "bg-amber-50 text-amber-700", gray: "bg-gray-100 text-gray-600", red: "bg-red-50 text-red-700" };
  return <span className={`text-[10px] uppercase tracking-wide px-2 py-1 ${tones[tone]}`}>{children}</span>;
}
